import { useState, useEffect } from "react";
import axios from "axios";
import {
  Search, Users, Shield, ChevronLeft, ChevronRight, Edit3, X, Check
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLAN_OPTIONS = ["trial", "base", "unlimited"];
const ROLE_OPTIONS = ["user", "admin"];

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [page, setPage] = useState(0);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const limit = 20;

  useEffect(() => { fetchUsers(); }, [page, planFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: page * limit, limit, search, plan: planFilter });
      const res = await axios.get(`${API}/admin/users?${params}`);
      setUsers(res.data.users);
      setTotal(res.data.total);
    } catch { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    fetchUsers();
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setEditForm({
      role: u.role,
      plan: u.subscription?.plan || "trial",
      scans_limit: u.usage?.scans_limit || 50,
      addon_scans: u.usage?.addon_scans || 0,
      status: u.subscription?.status || "active",
    });
  };

  const handleSave = async () => {
    try {
      const res = await axios.patch(`${API}/admin/users/${editingUser.id}`, editForm);
      setUsers(prev => prev.map(u => u.id === editingUser.id ? res.data : u));
      setEditingUser(null);
      toast.success("User updated");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to update"); }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
    catch { return ""; }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="page-container fade-in" data-testid="admin-users-page">
      <div className="page-header">
        <div><h1>User Management</h1><p className="page-subtitle">{total} total users</p></div>
      </div>

      {/* Search & Filters */}
      <div className="admin-toolbar" data-testid="admin-users-toolbar">
        <form onSubmit={handleSearch} className="jt-search-wrap" style={{ flex: 1 }}>
          <Search size={16} />
          <input className="jt-search-input" placeholder="Search by name or email..." value={search}
            onChange={e => setSearch(e.target.value)} data-testid="admin-users-search" />
        </form>
        <div className="jt-filters" style={{ flexWrap: "nowrap" }}>
          <button className={`jt-filter-btn ${!planFilter ? "active" : ""}`} onClick={() => { setPlanFilter(""); setPage(0); }}
            data-testid="admin-filter-all">All</button>
          {PLAN_OPTIONS.map(p => (
            <button key={p} className={`jt-filter-btn ${planFilter === p ? "active" : ""}`}
              onClick={() => { setPlanFilter(p); setPage(0); }} data-testid={`admin-filter-${p}`}>
              {p === "trial" ? "Trial" : p === "base" ? "Base" : "Unlimited"}
            </button>
          ))}
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "3rem" }}><div className="processing-spinner" /></div>
      ) : (
        <div className="admin-table-wrap" data-testid="admin-users-table">
          <table className="admin-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Plan</th>
                <th>Scans</th>
                <th>Role</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                  <td>
                    <div className="admin-user-cell">
                      <div className="admin-user-name">{u.name}</div>
                      <div className="admin-user-email">{u.email}</div>
                    </div>
                  </td>
                  <td><span className={`admin-plan-pill ${u.subscription?.plan || "trial"}`}>{u.subscription?.plan || "trial"}</span></td>
                  <td>{u.usage?.scans_used || 0} / {u.usage?.scans_limit || 0}{(u.usage?.addon_scans || 0) > 0 ? ` (+${u.usage.addon_scans})` : ""}</td>
                  <td>{u.role === "admin" ? <span className="admin-role-badge"><Shield size={10} /> Admin</span> : "User"}</td>
                  <td>{formatDate(u.created_at)}</td>
                  <td>
                    <button className="btn-icon" onClick={() => openEdit(u)} data-testid={`admin-edit-${u.id}`}><Edit3 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="admin-pagination" data-testid="admin-pagination">
          <button disabled={page === 0} onClick={() => setPage(p => p - 1)}><ChevronLeft size={16} /></button>
          <span>Page {page + 1} of {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}><ChevronRight size={16} /></button>
        </div>
      )}

      {/* Edit Modal */}
      {editingUser && (
        <div className="modal-overlay" onClick={() => setEditingUser(null)} data-testid="admin-edit-modal">
          <div className="modal-card" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit User — {editingUser.name}</h2>
              <button className="btn-icon" onClick={() => setEditingUser(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="modal-field">
                <label>Email</label>
                <div className="admin-edit-readonly">{editingUser.email}</div>
              </div>
              <div className="modal-row">
                <div className="modal-field">
                  <label>Role</label>
                  <select className="modal-input" value={editForm.role}
                    onChange={e => setEditForm({ ...editForm, role: e.target.value })} data-testid="admin-edit-role">
                    {ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div className="modal-field">
                  <label>Plan</label>
                  <select className="modal-input" value={editForm.plan}
                    onChange={e => setEditForm({ ...editForm, plan: e.target.value })} data-testid="admin-edit-plan">
                    {PLAN_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div className="modal-row">
                <div className="modal-field">
                  <label>Scan Limit</label>
                  <input className="modal-input" type="number" value={editForm.scans_limit}
                    onChange={e => setEditForm({ ...editForm, scans_limit: e.target.value })} data-testid="admin-edit-scans" />
                </div>
                <div className="modal-field">
                  <label>Add-on Scans</label>
                  <input className="modal-input" type="number" value={editForm.addon_scans}
                    onChange={e => setEditForm({ ...editForm, addon_scans: e.target.value })} data-testid="admin-edit-addon" />
                </div>
              </div>
              <div className="modal-field">
                <label>Status</label>
                <select className="modal-input" value={editForm.status}
                  onChange={e => setEditForm({ ...editForm, status: e.target.value })} data-testid="admin-edit-status">
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setEditingUser(null)}>Cancel</button>
              <button className="dash-cta-btn" onClick={handleSave} data-testid="admin-edit-save"><Check size={14} /> Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
